import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../enums/occurrenceStep";
import { OccurrenceStepApi } from "../models/indicators";
import {
  Occurrence,
  OccurrenceDispatch,
  OccurrenceFailure,
  OccurrenceStep,
  WorklogItem,
} from "../models/occurrence";
import { ServiceBayScheduleDto } from "./serviceBayDto";
import { OccurrenceTypeLabelEnum } from "../enums/occurrenceType";
import { getProtocolNumber, parseDate } from "../utils/kanbanUtils";
import { extractContractModality, extractContractNumber } from "../utils/contractUtils";
import { toUtcDateString } from "../utils/dateUtils";
import { optionsOccurrenceTypeEnum } from "../hooks/useDispatch";

export const OccurrenceStepDto = (data: any, occurrence: { osNumber?: string }): OccurrenceStep => ({
  id: data.id,
  name: OccurrenceStepLabelEnum[
    Object.keys(OccurrenceStepEnum).find(
      (key) => OccurrenceStepEnum[key] === data.stepId
    ) as keyof typeof OccurrenceStepLabelEnum
  ],
  step: data.stepId as OccurrenceStepEnum,
  report: data.report,
  observation: data.observation,
  dtStart: data.dtStart ? formatDate(data.dtStart + "Z", occurrence) : undefined,
  expectedEndDate: data.expectedDtEnd ? formatDate(data.expectedDtEnd + "Z", occurrence) : undefined,
  dtEnd: data.dtEnd ? formatDate(data.dtEnd + "Z", occurrence) : undefined,
  estimatedTime: data.estimatedTime,
  latest: data.latest ?? 0,
  updatedBy: data.updatedBy,
  updatedAt: data.updatedAt ? formatDate(data.updatedAt + "Z", occurrence) : undefined,
  updatedByUuid: data.updatedByUuid,
});

export const SummaryOccurrenceStepDto = (data: any, occurrence: { osNumber?: string }): OccurrenceStep => ({
  id: data.id,
  name: data.stepDescription,
  step: data.stepName as OccurrenceStepEnum,
  dtStart: data.startDate ? formatDate(data.startDate + "Z", occurrence) : undefined,
  dtEnd: data.endDate ? formatDate(data.endDate + "Z", occurrence) : undefined,
  estimatedTime: data.estimatedTime,
  latest: data.latest ?? 0,
});

export const OccurrenceDto = (data: any): Occurrence => {
  const toBoolean = (value: string) =>
    value === "true" || value === "-"
      ? true
      : value === "false"
        ? false
        : undefined;

  // Mapeia os campos do JSON para a estrutura da Occurrence
  const mapOccurrenceType = (type: string) => {
    if (type === "Guincho") return "W";
    return type;
  };

  return {
    id: data.uuid,
    criticality: data.criticality,
    osNumber: data.osNumber,
    osDtOpenAt: data.osDtOpenAt ? formatDate(toUtcDateString(data.osDtOpenAt), data) : undefined,
    createdBy: data.createdBy,
    literatureDiagnosis: data.literatureDiagnosis,
    literatureTroubleshooting: data.literatureTroubleshooting,
    country: data.country,
    occurrenceType: mapOccurrenceType(data.occurrenceType ?? data.type ?? data.occurrence_type ?? data.occurrenceTypeCode),
    tasNumber: data.tasNumber,
    tasStatus: data.tasStatus,
    timeOpenProtocol: data.timeOpenProtocol,
    source: data.source,
    hasLink: data.hasLink,
    mainOccurrence: toBoolean(data.mainOccurrence),
    scheduleId: data.scheduleUuid,
    estimateTimeRepair: data.estimateTimeRepair,
    solutionProposed: data.solutionProposed,
    mechanicLocation: data.mechanicLocation,
    towTruckLocation: data.towTruckLocation,
    requesterName: data.requesterName,
    requesterRole: data.requesterRole,
    requesterPhone: data.requesterPhone,
    onSitePersonName: data.onSitePersonName,
    onSitePersonRole: data.onSitePersonRole,
    onSitePersonPhone: data.onSitePersonPhone,
    checklist: data.checklist,
    customer: data.customer,
    renter: data.renter,
    scheduleNumber: data.scheduleNumber,
    protocolNumber: getProtocolNumber(data),
    observation: data.observation,
    status: data.status,
    finished: data.finished,
    stepTypeCurrent: data.stepTypeCurrent,
    dynamicsOccurrenceCode: data.dynamicsOccurrenceCode,
    version: data.version,

    // Campos de pedido de peça
    partOrderStatus: data.partOrder?.status,

    partOrderNumber: data.partOrder?.orderNumber,
    partOrderStatusOrder: data.partOrder?.statusOrder,

    // Veículo
    vehicleId: data.vehicle?.assetId,
    vehicleChassis: data.vehicle?.chassis,
    vehicleModel: data.vehicle?.modelId ?? data.vehicle?.vehicleModel, 
    vehicleModelCode: data.vehicle?.modelCode, 
    model: data.vehicle?.model, 
    vehicleLicensePlate: data.vehicle?.licensePlate,
    vehicleType: data.vehicle?.vehicleType,
    vehicleName: data.vehicle?.name,
    vehicleYear: data.vehicle?.vehicleYear,
    vehicleSaleDate: data.vehicle?.saleDate ? new Date(data.vehicle?.saleDate) : undefined,
    vehicleOdometer: data.vehicle?.odometer,
    vehicleHourmeter: data.vehicle?.hourMeter,
    truckerPayPlanPlanId: data.truckerPayPlanPlanId,
    contractNumber: extractContractNumber(data),
    contractModality: extractContractModality(data),
    vehiclePayloadType: data.vehicle?.payloadType,
    vehicleMaximumPayload: data.vehicle?.maximumPayload,
    vehicleCriticalPayload: data.vehicle?.criticalPayload,
    vehicleStopped: data.vehicle?.stopped,
    emissionStandard: data.vehicle?.emissionStandard ?? data.vehicle?.legislation,
    accountName: data.vehicle?.accountName,

    // Motorista
    driverName: data.driver?.name,
    driverLicenseNumber: data.driver?.driverLicenseNumber,
    driverPhone: data.driver?.phone,
    driverCheckInDriver: data.driver?.checkInDriver,

    // Concessionária
    dealershipDn: data.dealership?.dn,
    dealershipName: data.dealership?.name,
    dealershipPhone: data.dealership?.cellNumber,
    dealershipDemarcatedArea: data.dealership?.area,
    dealershipLocation: data.dealership?.local,
    dealershipRegional: data.dealership?.regional,
    dealershipCell: data.dealership?.cell,
    dealershipRepresentative: data.dealership?.representative,

    // Módulos
    modules: data.dtcs?.map((module: any) => ({
      id: module.id,
      name: module.name,
      spn: module.spn,
      fmi: module.fmi,
    })),

    // Etapas no formato CRU (igual da API)
    occurrenceSteps: (data.occurrenceSteps ?? []) as OccurrenceStepApi[],

    // Etapas
    steps: processOccurrenceSteps(data.occurrenceSteps, false, data),

    // Pedidos de peças
    partOrders: data.partOrders?.map((part: any) => ({
      number: part.number,
      status: part.status,
      date: parseDate(part.date),
    })),

    // Peças
    partOrderParts: data.partOrder?.occurrenceParts?.map((part: any) => ({
      partNumber: (() => {
        if (typeof part.partNumber === "string") {
          try {
            // Try to parse as JSON, if possible
            const parsed = JSON.parse(part.partNumber);
            // If parsed is an object and has code/description, return parsed
            if (
              parsed &&
              typeof parsed === "object" &&
              ("code" in parsed || "description" in parsed)
            ) {
              return parsed;
            }
            // If not, return as string
            return part.partNumber;
          } catch (e) {
            // Not a JSON string, return as is
            return part.partNumber;
          }
        }
        // If already an object, return as is
        return part.partNumber;
      })(),
      quantity: part.quantity,
    })),

    // Datas previstas e entregas da peça
    partOrderDeliveryDates: data.partOrderDeliveryDates?.map((part: any) => ({
      expected: part.expected ? parseDate(part.expected) : undefined,
      delivered: part.delivered ? parseDate(part.delivered) : undefined,
    })),

    partnerId: data.partnerId,

    createdAt: data.createdAt ? formatDate(data.createdAt, data) : undefined,
    updatedAt: data.updatedAt ? formatDate(data.updatedAt, data) : undefined,

    dispatches: (data.dispatches ?? []).map(OccurrenceDispatchDto),

    serviceBaySchedule: data.serviceBaySchedule?.active
      ? ServiceBayScheduleDto(data.serviceBaySchedule)
      : undefined,

    createdByUserProfileId: data.createdByUserProfileId,

    failures: data.failures?.map(OccurrenceFailureDto),

    finalizationReasonType: data.finalizationReasonType,
    finalizationReasonDescription: data.finalizationReasonDescription,

    // Entrada e saída do veículo
    vehicleEntryAt: data.vehicleEntryAt ? formatDate(data.vehicleEntryAt, data) : undefined,
    vehicleExitAt: data.vehicleExitAt ? formatDate(data.vehicleExitAt, data) : undefined,
    
    volksEssentialsPlanContract: data.volksEssentialsPlanContract,
    volksEssentialsPlanRevisionSku: data.volksEssentialsPlanRevisionSku,
    // Itens do agendamento (peças cobertas, outros serviços e observações gerais)
    coveredItems: data.appointmentItems?.coveredItems?.map((item: any) => ({
      id: item.id,
      code: item.code,
      description: item.description,
      qtd: item.qtd,
      isCovered: item.isCovered,
      notCoveredReason: item.notCoveredReason,
      ref: item.ref,
    })),
    otherServices: data.appointmentItems?.otherServices?.map((service: any) => ({
      id: service.id,
      name: service.name,
      price: service.price,
    })),
    lastWorklog: mapWorklogItem(data.lastWorklog),
    generalObservations: data.appointmentItems?.generalObservations,
  };
};

const mapWorklogItem = (worklogItem: any) : WorklogItem | null => {
  if(worklogItem == null) {
    return null
  }
  return {
    updatedAt: worklogItem.updatedAt ? new Date(worklogItem.updatedAt) : null,
    updatedBy: worklogItem.updatedBy
  }
}

const removeDuplicatedStepsWithoutEnd = (steps: any[], isFinished?: boolean) => {
  const stepsWithoutEnd = steps.filter((step: any) => !step.endDate && !step.dtEnd);

  return steps
    .filter((step: any) => isFinished ? step.endDate : true)
    .map((step: any) => {
      const withoutEndIndex = stepsWithoutEnd.findIndex((stepWithoutEnd: any) => stepWithoutEnd.stepName === step.stepName && stepWithoutEnd.dtStart === step.dtStart)
      if (withoutEndIndex === -1) return step;
      else if (withoutEndIndex === 0) return step;
      return null;
    }).filter(Boolean)
}

export const SummaryOccurrenceDto = (data: any): Occurrence => {

  return {
    id: data.uuid,
    vehicleChassis: data.chassis,
    vehicleId: data.assetId,
    vehicleModel: data.modelId, 
    vehicleModelCode: data.modelCode, 
    model: data.model, 
    vehicleName: data.name,
    vehicleLicensePlate: data.licensePlate,
    vehicleOdometer: data.odometer,
    vehicleHourmeter: data.hourMeter,
    criticality: data.criticality,
    scheduleId: data.scheduleUuid,
    protocolNumber: getProtocolNumber(data),
    scheduleNumber: data.scheduleNumber,
    steps: processOccurrenceSteps(removeDuplicatedStepsWithoutEnd(data.steps, data.finished || !!data.endDate), true, data),
    serviceBaySchedule: data.serviceBaySchedule?.active
      ? ServiceBayScheduleDto(data.serviceBaySchedule)
      : undefined,
    finished: data.endDate || data.finished ? true : false,
    createdByUserProfileId: data.createdByUserProfileId,
    version: data.version,
    dealershipName: data.dealership,
    occurrenceType: OccurrenceTypeLabelEnum[data.occurrenceType as keyof typeof OccurrenceTypeLabelEnum],
  };
};

export const OccurrenceDispatchDto = (data: any): OccurrenceDispatch => ({
  id: data.uuid,
  occurrenceType: optionsOccurrenceTypeEnum[data.type as keyof typeof optionsOccurrenceTypeEnum],
  payer: data.payer,
  authorizePayment: data.authorizePayment,
  dn: data.dn,
  createdAt: data.createdAt ? formatDate(data.createdAt, data) : undefined,
  status: data.status,
  route: data.route,
  trip: data.trip,
  tripStatus: data.tripStatus,
  reasonRefusal: data.reasonRefusal,
  descriptionRefusal: data.descriptionRefusal,
  driver: data.driver,
  driverName: data.driverName,
  originLocation: data.originLocation,
  destinationLocation: data.destinationLocation,
  cancelledBy: data?.cancelledBy,
  protocol: data.protocol,
  assistedCustomer: data.assistedCustomer,
  updatedAt: data?.updatedAt ? new Date(data.updatedAt) : undefined,
});

export const OccurrenceFailureDto = (data: any): OccurrenceFailure => ({
  failureType: data.failureType,
  failureMode: data.failureMode,
  failureChecked: data.failureChecked,
});

export const processOccurrenceSteps = (
  data: any,
  isSummary: boolean = false,
  occurrence: { osNumber?: string } = {}
): OccurrenceStep[] => {
  if (!data) return [];

  // Agrupa os steps por step e mantém apenas o mais recente
  const groupedSteps = (data ?? []).reduce((acc: any, step: any) => {
    const stepKey = isSummary ? step.stepName : step.stepId;
    if (!acc[stepKey]) {
      acc[stepKey] = [];
    }
    acc[stepKey].push(step);
    return acc;
  }, {});

  // Para cada grupo, ordena por dtStart e mantém apenas o mais recente
  return Object.values(groupedSteps).map((stepGroup: any) => {
    // Ordena por dtStart (mais recente primeiro)
    const sortedSteps = stepGroup.sort((a: any, b: any) => {
      const dateA = (a.startDate || a.dtStart) ? formatDate((a.startDate || a.dtStart) + "Z", occurrence) : new Date(0);
      const dateB = (b.startDate || b.dtStart) ? formatDate((b.startDate || b.dtStart) + "Z", occurrence) : new Date(0);
      return (dateB?.getTime() ?? 0) - (dateA?.getTime() ?? 0);
    });

    const latestStep = sortedSteps[0];
    const olderSteps = sortedSteps.slice(1);

    // Cria logs para os steps mais antigos
    const logs = olderSteps.map((step: any) => ({
      startDate: (step.startDate || step.dtStart) ? formatDate((step.startDate || step.dtStart) + "Z", occurrence) : new Date(),
      endDate: (step.endDate || step.dtEnd) ? formatDate((step.endDate || step.dtEnd) + "Z", occurrence) : new Date(),
      checkInBy: null,
      checkInByUuid: null,
      checkOutBy: null,
      checkOutByUuid: null,
    }));

    return {
      ...(isSummary
        ? SummaryOccurrenceStepDto(latestStep, occurrence)
        : OccurrenceStepDto(latestStep, occurrence)),
      logs: [
        ...logs,
        {
          startDate:
            latestStep.startDate || latestStep.dtStart
              ? formatDate((latestStep.startDate || latestStep.dtStart) + "Z", occurrence)
              : new Date(),
          endDate:
            latestStep.endDate || latestStep.dtEnd
              ? formatDate((latestStep.endDate || latestStep.dtEnd) + "Z", occurrence)
              : new Date(),
          checkInBy: null,
          checkInByUuid: null,
          checkOutBy: null,
          checkOutByUuid: null,
        },
      ].sort((a: any, b: any) => a.startDate.getTime() - b.startDate.getTime()),
    };
  });
};

const formatDate = (dateString: string | undefined, occurrence: { osNumber?: string }, applyMidnightFix = false): Date | undefined => {
  if (!dateString) return undefined;

  const date = new Date(dateString);

  // Date-only strings (time is midnight) are parsed by JS as UTC 00:00 regardless of
  // local timezone, so they land 3h off in America/Sao_Paulo (UTC-3). Scoped to the
  // work order's own open date (osDtOpenAt) only — not step dates, which are parsed
  // differently (forced UTC via "Z") and are out of scope for this fix.
  if (applyMidnightFix) {
    const isMidnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
    if (isMidnight) {
      date.setHours(date.getHours() + 3);
    }
  }

  return date;
}

